'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTechnology } from '@/hooks/technology';
import type { ISerialPort } from '@/hooks/useSerialPort';
import { fetchWithAuth, getUserFromToken } from '@/lib/api';
import { sleep } from '@/lib/utils';
import type { ITechnology } from '@/types/production/tool';

namespace Namespace {
  export interface UseAutoTestProps {
    technology: ITechnology | null;
  }

  export interface Detected {
    // Identificador estável da porta — usado como key na UI (serial pode faltar ou repetir).
    id: string;
    port: ISerialPort;
    equipment?: Equipment | undefined;
    status: 'fully_identified' | 'partially_identified' | 'not_identified';
  }

  export interface Equipment {
    imei?: string | undefined;
    iccid?: string | undefined;
    firmware?: string | undefined;
    serial?: string | undefined;
  }

  export interface AutoTestLogInput {
    equipment: {
      serial: string;
      firmware: string;
      imei?: string;
      iccid?: string;
      lora_version?: string;
      ble_version?: string;
      // Nomes alinhados com o schema Zod da rota /auto-test-logs/batch.
      lora_keys?: {
        dev_eui?: string;
        app_eui?: string;
        app_key?: string;
        dev_addr?: string;
        nwk_s_key?: string;
        app_s_key?: string;
      };
    };
    technology: {
      id: string;
      system_name: string;
    };
    user: {
      id: string;
      name: string;
    };
    status: boolean;
    analysis: Record<string, boolean>;
  }

  export interface AutoTest extends AutoTestLogInput {
    id: string;
    created_at: Date | string;
  }

  export interface AutoTestResult {
    port: ISerialPort;
    response?: unknown;
    messages?: ReadonlyArray<{ key: string; command: string }>;
    end_time?: number;
    init_time?: number;
    analysis?: Record<string, boolean>;
    status?: boolean;
    // Dados lidos durante o teste (ex.: versões LoRa/BLE do BWS 4G+LoRa)
    equipment?: { lora_version?: string; ble_version?: string };
  }

  /** Motivo pelo qual um resultado de auto teste não pôde virar log. */
  export type DiscardReason =
    | 'incomplete_result'
    | 'unknown_equipment'
    | 'missing_identification'
    | 'duplicate_port'
    | 'no_user';

  export interface Discarded {
    port: ISerialPort;
    serial?: string | undefined;
    reason: DiscardReason;
  }
}

type AutoTestLogCreate = Namespace.AutoTestLogInput;

type MapResult =
  | { ok: true; log: AutoTestLogCreate }
  | { ok: false; reason: Namespace.DiscardReason; serial?: string | undefined };

function mapAutoTestResultToLog(
  result: Namespace.AutoTestResult,
  // Indexado por porta: a porta é a única identidade estável de um dispositivo
  // conectado. O serial é dado lido do equipamento — pode faltar ou repetir
  // entre unidades ainda não identificadas.
  detectedByPort: Map<ISerialPort, Namespace.Detected>,
  technology: ITechnology,
): MapResult {
  const detectedDevice = detectedByPort.get(result.port);
  const serial = detectedDevice?.equipment?.serial;

  // Verifica campos obrigatórios
  if (
    !result.response ||
    !result.messages ||
    !result.end_time ||
    !result.init_time ||
    !result.analysis ||
    typeof result.status !== 'boolean'
  ) {
    return { ok: false, reason: 'incomplete_result', serial };
  }

  // `analysis: {}` é truthy: sem esta checagem, uma falha de comunicação na
  // porta (que retorna o template com analysis vazio e status false) era
  // gravada como reprovação legítima do equipamento.
  if (Object.keys(result.analysis).length === 0) {
    return { ok: false, reason: 'incomplete_result', serial };
  }

  const equipment = detectedDevice?.equipment;

  if (!equipment) return { ok: false, reason: 'unknown_equipment', serial };

  // serial e firmware são obrigatórios no log; sem eles o registro é inútil
  // para rastreabilidade e o backend rejeitaria o payload.
  if (!equipment.serial || !equipment.firmware) {
    return { ok: false, reason: 'missing_identification', serial };
  }

  const user = getUserFromToken();
  if (!user) return { ok: false, reason: 'no_user', serial };

  return {
    ok: true,
    log: {
      analysis: result.analysis,
      equipment: {
        imei: equipment.imei,
        firmware: equipment.firmware,
        serial: equipment.serial,
        iccid: equipment.iccid,
        lora_version: result.equipment?.lora_version,
        ble_version: result.equipment?.ble_version,
      },
      status: result.status,
      technology: {
        id: technology.id,
        system_name: technology.name.system,
      },
      user,
    },
  };
}

const DISCARD_MESSAGES: Record<Namespace.DiscardReason, string> = {
  incomplete_result: 'comunicação incompleta com o dispositivo',
  unknown_equipment: 'dispositivo não corresponde a nenhuma porta detectada',
  missing_identification: 'serial ou firmware ausente',
  duplicate_port: 'mesmo IMEI detectado em outra porta — o equipamento foi testado só uma vez',
  no_user: 'sessão expirada — faça login novamente',
};

const DETECTION_INTERVAL_MS = 5000;

// Portas já testáveis mas não totalmente identificadas (ex.: sem SIM o ICCID não vem) são
// sondadas de novo com intervalo maior e um limite de tentativas: pega o ICCID se o modem
// ler o SIM depois, sem manter a detecção rodando para sempre na bancada.
const PARTIAL_REPROBE_INTERVAL_MS = 20_000;
const PARTIAL_MAX_REPROBES = 3;

interface PartialProbe {
  count: number;
  last: number;
}

let nextPortId = 0;
const portIds = new WeakMap<ISerialPort, string>();

const getPortId = (port: ISerialPort): string => {
  let id = portIds.get(port);
  if (!id) {
    nextPortId += 1;
    id = `port-${nextPortId}`;
    portIds.set(port, id);
  }
  return id;
};

/** Serial e firmware são o mínimo para rodar o auto teste e gravar o log. */
export const isTestable = (device: Namespace.Detected): boolean =>
  Boolean(device.equipment?.serial && device.equipment?.firmware);

/**
 * Escolhe as portas a sondar neste ciclo:
 * - sem identificação suficiente para testar → todo ciclo (o device pode estar ligando);
 * - totalmente identificada → nunca;
 * - testável mas parcial → a cada PARTIAL_REPROBE_INTERVAL_MS, até PARTIAL_MAX_REPROBES vezes.
 */
export function selectPortsToDetect(
  ports: ISerialPort[],
  detected: Namespace.Detected[],
  probes: ReadonlyMap<ISerialPort, PartialProbe>,
  now: number,
): ISerialPort[] {
  const byPort = new Map(detected.map((device) => [device.port, device]));
  return ports.filter((port) => {
    const device = byPort.get(port);
    if (!device || !isTestable(device)) return true;
    if (device.status === 'fully_identified') return false;
    const probe = probes.get(port);
    if (!probe) return true;
    return probe.count < PARTIAL_MAX_REPROBES && now - probe.last >= PARTIAL_REPROBE_INTERVAL_MS;
  });
}

/**
 * Uma re-sondagem pode ler menos campos que a anterior (ex.: resposta truncada); mantém o que
 * já foi lido em vez de rebaixar a identificação. Serial diferente = outro equipamento.
 */
export function mergeEquipment(
  previous: Namespace.Equipment | undefined,
  next: Namespace.Equipment,
): Namespace.Equipment {
  if (!previous || previous.serial !== next.serial) return next;
  const defined = Object.fromEntries(Object.entries(next).filter(([, v]) => v !== undefined));
  return { ...previous, ...defined };
}

/**
 * Dispositivos que o auto teste vai rodar. O mesmo IMEI em mais de uma porta (equipamento que
 * expõe duas COM) é testado uma vez só, preferindo a entrada totalmente identificada. Só o IMEI
 * é usado: serial de fábrica pode repetir entre unidades diferentes.
 */
export function selectTestableDevices(detected: Namespace.Detected[]): {
  testable: Namespace.Detected[];
  duplicates: Namespace.Detected[];
} {
  const testable: Namespace.Detected[] = [];
  const duplicates: Namespace.Detected[] = [];
  const indexByImei = new Map<string, number>();

  for (const device of detected.filter(isTestable)) {
    const imei = device.equipment?.imei;
    const index = imei ? indexByImei.get(imei) : undefined;

    if (index === undefined) {
      if (imei) indexByImei.set(imei, testable.length);
      testable.push(device);
    } else if (
      device.status === 'fully_identified' &&
      testable[index].status !== 'fully_identified'
    ) {
      duplicates.push(testable[index]);
      testable[index] = device;
    } else {
      duplicates.push(device);
    }
  }

  return { testable, duplicates };
}

export const useAutoTest = (props: Namespace.UseAutoTestProps) => {
  const { technology } = props;
  const [detected, setDetected] = useState<Namespace.Detected[]>([]);
  const isIdentifying = useRef<boolean>(false);

  // Espelho de `detected` para o loop de detecção decidir quais portas re-detectar
  // sem recriar o interval a cada mudança de estado.
  const detectedRef = useRef<Namespace.Detected[]>([]);
  useEffect(() => {
    detectedRef.current = detected;
  }, [detected]);

  const [autoTest, setAutoTest] = useState<Namespace.AutoTest[]>([]);
  const isAutoTesting = useRef(false);

  const [detectionProgress, setDetectionProgress] = useState(false);
  const [autoTestProgress, setAutoTestProgress] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [discarded, setDiscarded] = useState<Namespace.Discarded[]>([]);

  // hook that handle interactions with devices
  const { ports, handleDetection, handleAutoTest, requestPort, isIdentified } =
    useTechnology(technology);

  // handleDetection/isIdentified mudam de identidade a cada render (useSerialPort não memoiza
  // getReader/openPort). Como deps do efeito de detecção, qualquer render durante a leitura
  // (~11s) reiniciava o efeito e o resultado do ciclo era descartado em silêncio — a detecção
  // demorava e o indicador ficava preso em "detectando". O efeito lê a versão atual por ref.
  const portsRef = useRef(ports);
  const handleDetectionRef = useRef(handleDetection);
  const isIdentifiedRef = useRef(isIdentified);
  useEffect(() => {
    portsRef.current = ports;
    handleDetectionRef.current = handleDetection;
    isIdentifiedRef.current = isIdentified;
  });

  const technologyId = technology?.id;

  const testableCount = useMemo(() => selectTestableDevices(detected).testable.length, [detected]);

  // function that handle the auto test process, check if the process was successful and save result on database
  const test = useCallback(async () => {
    if (!technology) return;

    // update isAutoTesting reference to true to prevent multiple auto test processes
    // (impede que o loop de detecção inicie uma NOVA detecção durante o autotest)
    isAutoTesting.current = true;
    setAutoTestProgress(true);
    setError(null);
    setDiscarded([]);

    try {
      // Snapshot indexado por porta. A porta é a identidade estável do
      // dispositivo; usar o serial como chave faz unidades sem serial (ou com
      // serial de fábrica repetido) colidirem, o que trocava ou descartava
      // silenciosamente resultados quando vários dispositivos eram testados juntos.
      const detectedByPort = new Map(detected.map((device) => [device.port, device]));

      const { testable, duplicates } = selectTestableDevices(detected);
      const testablePorts = testable.map(({ port }) => port);
      const duplicateDiscards: Namespace.Discarded[] = duplicates.map((device) => ({
        port: device.port,
        serial: device.equipment?.serial,
        reason: 'duplicate_port',
      }));

      if (!testablePorts.length) {
        setError('Nenhum dispositivo identificado o suficiente para testar.');
        return;
      }

      // Aguarda uma detecção JÁ em andamento terminar antes de começar o autotest.
      // Sem isso, a leitura da detecção (aberta por até 10s) e o autotest concorrem na
      // mesma porta — o device emite serial break ao entrar em modo de teste e a detecção
      // recebe "BreakError: Break received". Espera limitada (~11s) para não travar a UI.
      const waitStart = Date.now();
      while (isIdentifying.current && Date.now() - waitStart < 11000) {
        await sleep(200);
      }

      // run auto test devices
      const autoTestResult = await handleAutoTest(testablePorts);

      // Mantém o resultado original junto do mapeamento para que cada descarte
      // continue associado à sua porta de origem sem depender de índices.
      const mapped = autoTestResult.map((testResult) => ({
        testResult,
        mapped: mapAutoTestResultToLog(testResult, detectedByPort, technology),
      }));

      const result = mapped.flatMap(({ mapped: el }) => (el.ok ? [el.log] : []));

      const skipped = mapped.flatMap(({ testResult, mapped: el }) =>
        el.ok ? [] : [{ port: testResult.port, serial: el.serial, reason: el.reason }],
      );

      if (duplicateDiscards.length || skipped.length) {
        setDiscarded([...duplicateDiscards, ...skipped]);
      }

      if (!result.length) {
        setError(
          `Nenhum resultado pôde ser registrado (${skipped.length} descartado(s)). ` +
            'Verifique a identificação dos dispositivos.',
        );
        return;
      }

      // save result on database
      const res = await fetchWithAuth('/api/production/auto-test-logs/batch', {
        method: 'POST',
        body: JSON.stringify({ logs: result }),
      });
      if (!res.ok) throw new Error(`Erro ao salvar log de auto test (HTTP ${res.status})`);

      const json = (await res.json()) as Namespace.AutoTest[] | { data?: Namespace.AutoTest[] };
      // O backend Fastify responde { success, statusCode, data: [...] } (response.helper).
      // Concatenar o wrapper inteiro criava um item "fantasma" na tabela (sem id/equipment/
      // analysis → "-- reprovado 0/0"). Extrai o array de logs criados; aceita array puro
      // como fallback para compatibilidade.
      const savedLogs: Namespace.AutoTest[] = Array.isArray(json) ? json : (json.data ?? []);

      // update state with configuration process result
      setAutoTest((prev) => prev.concat(savedLogs));

      if (skipped.length) {
        setError(
          `${skipped.length} de ${autoTestResult.length} resultado(s) não foram registrados.`,
        );
      }
    } catch (err) {
      console.error('Error during auto test:', err);
      setError(err instanceof Error ? err.message : 'Falha inesperada durante o auto teste.');
    } finally {
      isAutoTesting.current = false;
      setAutoTestProgress(false);
    }
  }, [handleAutoTest, detected, technology]);

  // Reinicia só quando a tecnologia muda (ou no unmount): o resultado de um ciclo em
  // andamento com o protocolo antigo é descartado. Portas e funções vêm por ref.
  useEffect(() => {
    // Sem tecnologia não há protocolo para falar com o device — o fallback do useTechnology
    // (E3+ 4G) mandaria comandos de outro equipamento para todas as portas.
    if (!technologyId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    // Re-sondagens já feitas por porta parcialmente identificada (ver selectPortsToDetect).
    // Local ao efeito: tecnologia nova recomeça a contagem do zero.
    const probes = new Map<ISerialPort, PartialProbe>();

    // Agendamento recursivo em vez de setInterval: garante que um ciclo de
    // detecção só é agendado após o anterior terminar, e permite reavaliar
    // isAutoTesting depois do await. Com setInterval, uma detecção iniciada
    // logo antes do teste seguia disputando a mesma porta serial.
    // Guarda o cancelamento num único ponto: qualquer reagendamento após o
    // cleanup criaria um timer fora do alcance do clearTimeout.
    const scheduleNext = () => {
      if (cancelled) return;
      timer = setTimeout(runDetection, DETECTION_INTERVAL_MS);
    };

    const runDetection = async () => {
      if (cancelled) return;

      // Durante um auto teste a detecção fica suspensa: ela abriria as mesmas
      // portas que o teste está usando.
      if (isAutoTesting.current || isIdentifying.current) {
        scheduleNext();
        return;
      }

      const ports = portsRef.current;
      if (!ports.length) {
        if (detectedRef.current.length) {
          detectedRef.current = [];
          setDetected([]);
        }
        scheduleNext();
        return;
      }

      // Detecção seletiva: re-detectar todo mundo a cada 5s abria/fechava todas as portas
      // continuamente, atrasava o início do autotest e gerava churn de USB.
      const now = Date.now();
      const portsToDetect = selectPortsToDetect(ports, detectedRef.current, probes, now);
      for (const port of portsToDetect) {
        const probe = probes.get(port);
        if (probe) probes.set(port, { count: probe.count + 1, last: now });
      }

      isIdentifying.current = true;
      if (portsToDetect.length) setDetectionProgress(true);

      try {
        const results = portsToDetect.length
          ? (await handleDetectionRef.current(portsToDetect)).filter((d) => d.response?.serial)
          : [];

        // Um auto teste pode ter começado enquanto a detecção corria; descartar
        // o resultado evita sobrescrever o snapshot usado pelo teste em curso.
        // O `finally` abaixo ainda libera isIdentifying e reagenda o ciclo.
        if (cancelled || isAutoTesting.current) return;

        // Indexado por porta, não por serial: o serial pode faltar ou repetir
        // entre unidades não identificadas, o que fazia dois dispositivos
        // colidirem numa só entrada e trocava/descartava resultados.
        const map = new Map(detectedRef.current.map((d) => [d.port, d]));

        for (const { port, response } of results) {
          if (!response) continue;
          const equipment = mergeEquipment(map.get(port)?.equipment, response);
          const device: Namespace.Detected = {
            id: getPortId(port),
            port,
            equipment,
            status: isIdentifiedRef.current(equipment),
          };
          map.set(port, device);

          // Ficou testável mas parcial agora: a contagem de re-sondagens começa deste ponto.
          if (device.status !== 'fully_identified' && isTestable(device) && !probes.has(port)) {
            probes.set(port, { count: 0, last: Date.now() });
          }
        }

        // Portas desconectadas não devem permanecer na lista.
        const active = new Set(portsRef.current);
        const next = Array.from(map.values()).filter((d) => active.has(d.port));
        if (results.length || next.length !== detectedRef.current.length) {
          detectedRef.current = next;
          setDetected(next);
        }
      } catch (err) {
        console.error('Error during detection:', err);
      } finally {
        isIdentifying.current = false;
        setDetectionProgress(false);
        // scheduleNext não reagenda após o cleanup: um timer criado depois dele
        // ficaria fora do alcance do clearTimeout e manteria o ciclo vivo.
        scheduleNext();
      }
    };

    scheduleNext();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [technologyId]);

  return {
    autoTest,
    detected,
    // Dispositivos que o auto teste vai rodar (testáveis, sem IMEI repetido em outra porta).
    testableCount,
    test,
    requestPort,
    error,
    discarded,
    discardMessages: DISCARD_MESSAGES,
    isTesting: autoTestProgress,
    isDetecting: detectionProgress,
    progress: {
      autoTest: autoTestProgress,
      detection: detectionProgress,
    },
  };
};

export default useAutoTest;
